// /client/src/components/AdminDashboard/WhitelistManager.js
import React, { useState, useEffect, useCallback } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Link } from 'react-router-dom';
import './WhitelistManager.css';

const WhitelistManager = () => {
  const [domains, setDomains] = useState([]);
  const [emails, setEmails] = useState([]);
  const [filteredDomains, setFilteredDomains] = useState([]);
  const [filteredEmails, setFilteredEmails] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [newEntry, setNewEntry] = useState('');
  const [entryType, setEntryType] = useState('domain');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
const [showCreditModal, setShowCreditModal] = useState(false);
const [creditUserEmail, setCreditUserEmail] = useState('');
const [creditAmount, setCreditAmount] = useState('1');
const [creditError, setCreditError] = useState('');
const [creditSuccess, setCreditSuccess] = useState('');
const [showAllDomains, setShowAllDomains] = useState(false);
const [showAllEmails, setShowAllEmails] = useState(false);
const [inviteLinks, setInviteLinks] = useState([]);
const [filteredInviteLinks, setFilteredInviteLinks] = useState([]);
const [showLinkModal, setShowLinkModal] = useState(false);
const [newSlug, setNewSlug] = useState('');
const [newUsageLimit, setNewUsageLimit] = useState('10');
const [linkError, setLinkError] = useState('');
const [linkSuccess, setLinkSuccess] = useState('');
const [showAllLinks, setShowAllLinks] = useState(false);
// === NEW: Waiting list state ===
const [waitingList, setWaitingList] = useState([]);
const [filteredWaitingList, setFilteredWaitingList] = useState([]);
const [showAllWaiting, setShowAllWaiting] = useState(false);
const [approvingId, setApprovingId] = useState(null);
const [selectedLabel, setSelectedLabel] = useState('standard'); // 'standard' | 'coachplus' | 'leadershipplus'

  // Validate input
  const validateEntry = (value, type) => {
    if (!value) return 'Please enter a value';
    if (type === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value.trim())) return 'Invalid email format';
    }
    if (type === 'domain') {
      const domainRegex = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!domainRegex.test(value.trim())) return 'Invalid domain format (e.g., example.com)';
    }
    return '';
  };

 // === 1. Define fetchWaitingList FIRST ===
const fetchWaitingList = useCallback(async () => {
  const token = localStorage.getItem('adminToken');
  if (!token) {
    console.warn('No token found for waiting list fetch');
    setWaitingList([]);
    setFilteredWaitingList([]);
    return;
  }

  try {
    const res = await fetch('/api/whitelist/waiting-list', {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`
      }
    });

    if (res.ok) {
      const data = await res.json();
      setWaitingList(data);
      setFilteredWaitingList(data);
    } else if (res.status === 401 || res.status === 403) {
      console.warn('Unauthorized for waiting list');
      setWaitingList([]);
      setFilteredWaitingList([]);
    } else {
      console.error('Error fetching waiting list:', res.status);
      setWaitingList([]);
      setFilteredWaitingList([]);
    }
  } catch (err) {
    console.error('Network error fetching waiting list:', err);
    setWaitingList([]);
    setFilteredWaitingList([]);
  }
}, []);

// === 2. Now define fetchWhitelist (which calls fetchWaitingList) ===
const fetchWhitelist = useCallback(async () => {
  setLoading(true);
  setError(null);
  try {
    const token = localStorage.getItem('adminToken');

    const [domainsRes, emailsRes, linksRes] = await Promise.all([
      fetch(`/api/whitelist/domains`, { headers: { Accept: 'application/json' } }),
      fetch(`/api/whitelist/emails`, { headers: { Accept: 'application/json' } }),
      token ? fetch(`/api/whitelist/invite-links`, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`
        }
      }).catch(() => null) : new Response(null, { status: 401 })
    ]);

    // Handle domains & emails (public)
    if (!domainsRes.ok || !emailsRes.ok) {
      throw new Error('Failed to load public whitelist data');
    }

    const domainsData = await domainsRes.json();
    const emailsData = await emailsRes.json();
    setDomains(domainsData);
    setEmails(emailsData);
    setFilteredDomains(domainsData);
    setFilteredEmails(emailsData);

    // Handle invite links (admin-only)
    if (!linksRes || !linksRes.ok) {
      if (linksRes && (linksRes.status === 401 || linksRes.status === 403)) {
        console.warn('Not authorized to view invite links');
      }
      setInviteLinks([]);
      setFilteredInviteLinks([]);
    } else {
      const linksData = await linksRes.json();
      setInviteLinks(linksData);
      setFilteredInviteLinks(linksData);
    }

    // Now safely call fetchWaitingList
    fetchWaitingList();

  } catch (error) {
    console.error('Error in fetchWhitelist:', error);
    setError('Failed to load data. Please re-login as admin.');
    toast.error('Failed to load admin data');
    setInviteLinks([]);
    setFilteredInviteLinks([]);
    setWaitingList([]);
    setFilteredWaitingList([]);
  } finally {
    setLoading(false);
  }
}, [fetchWaitingList]); // ← Now safe to include as dependency

useEffect(() => {
  fetchWhitelist();
}, [fetchWhitelist]);

  // Filter domains and emails based on search term
  useEffect(() => {
    setFilteredDomains(
      domains.filter((d) => d.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredEmails(
      emails.filter((e) => e.toLowerCase().includes(searchTerm.toLowerCase()))
    );
setFilteredInviteLinks(
    inviteLinks.filter((link) =>
      link.slug.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (link.creator && link.creator.toLowerCase().includes(searchTerm.toLowerCase()))
    )
  );
  setFilteredWaitingList(
  waitingList.filter((item) =>
    item.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.slug && item.slug.toLowerCase().includes(searchTerm.toLowerCase()))
  )
);
}, [searchTerm, domains, emails, inviteLinks, waitingList]);

  // Handle adding a new whitelist entry
  const handleAddEntry = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validateEntry(newEntry, entryType);
    if (validationError) {
      setError(validationError);
      toast.error(validationError);
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/whitelist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ type: entryType, value: newEntry.trim(),  usertype: selectedLabel
 }),
      });

      if (response.ok) {
        setSuccessMessage(`${newEntry} added to whitelist.`);
        toast.success(`${newEntry} added to whitelist.`);
        setNewEntry('');
         setSelectedLabel('standard'); 
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchWhitelist();
      } else {
        const errorData = await response.json();
        console.error('Error adding entry:', errorData);
        setError(`Failed to add entry: ${errorData.message || 'Unknown error'}`);
        toast.error(`Failed to add entry: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error adding entry:', error);
      setError('Failed to add entry. Please try again.');
      toast.error('Failed to add entry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete confirmation
const confirmDelete = (type, value) => {
  setDeleteCandidate({ type, value });
  setShowDeleteModal(true);
};

  // Handle deleting a whitelist entry
 const handleDeleteEntry = async () => {
  if (!deleteCandidate) return;

  try {
    setLoading(true);
    const token = localStorage.getItem('adminToken');

    let endpoint = '/api/whitelist';
    if (deleteCandidate.type === 'inviteLink') {
      endpoint = `/api/whitelist/invite-links/${deleteCandidate.value}`;
    }

    const response = await fetch(endpoint, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`, // both delete targets are now auth-protected server-side
      },
      ...(deleteCandidate.type !== 'inviteLink' && {
        body: JSON.stringify({ type: deleteCandidate.type, value: deleteCandidate.value }),
      }),
    });

    if (response.ok) {
      toast.success(`${deleteCandidate.value} removed successfully.`);
      setShowDeleteModal(false);
      setDeleteCandidate(null);

      if (deleteCandidate.type === 'inviteLink') {
        // Refresh invite links
        const linksRes = await fetch('/api/whitelist/invite-links', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (linksRes.ok) {
          const linksData = await linksRes.json();
          setInviteLinks(linksData);
          setFilteredInviteLinks(linksData);
        }
      } else {
        fetchWhitelist();
      }
    } else {
      const errorData = await response.json();
      setError(`Failed: ${errorData.message || 'Unknown error'}`);
      toast.error(errorData.message || 'Failed');
    }
  } catch (error) {
    setError('Failed to delete. Please try again.');
    toast.error('Failed to delete.');
  } finally {
    setLoading(false);
  }
};

  
  const handleGrantInviteCredits = async (e) => {
  e.preventDefault();
  setCreditError('');
  setCreditSuccess('');

  const email = creditUserEmail.trim();
  const amount = parseInt(creditAmount, 10);

  if (!email) return setCreditError('Please enter a user email');
  if (isNaN(amount) || amount < 1) return setCreditError('Amount must be ≥ 1');

  try {
    setLoading(true);
    const token = localStorage.getItem('adminToken'); // consistent with other admin calls

    const response = await fetch('/api/admin/grant-invite-credits', {  // ← FIXED HERE
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ email, credits: amount }),
    });

    if (response.ok) {
      const data = await response.json();
      setCreditSuccess(`Granted ${amount} invite credit(s) to ${email}`);
      toast.success(`Granted ${amount} invite credit(s)`);
      setCreditUserEmail('');
      setCreditAmount('1');
      setShowCreditModal(false);
    } else {
      const err = await response.json();
      setCreditError(err.message || 'Failed to grant credits');
      toast.error(err.message || 'Failed');
    }
  } catch (err) {
    setCreditError('Network error');
    toast.error('Network error');
  } finally {
    setLoading(false);
  }
};

const handleCreateInviteLink = async (e) => {
  e.preventDefault();
  setLinkError('');
  setLinkSuccess('');

  const slug = newSlug.trim();
  const limit = parseInt(newUsageLimit, 10);

  if (!slug) return setLinkError('Please enter a slug');
  if (!/^[a-zA-Z0-9-]+$/.test(slug)) return setLinkError('Slug can only contain letters, numbers, and hyphens');
  if (isNaN(limit) || limit < 1) return setLinkError('Usage limit must be ≥ 1');

  try {
    setLoading(true);
    const token = localStorage.getItem('adminToken');

    const response = await fetch('/api/whitelist/invite-links', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ slug, usageLimit: limit }),
    });

    if (response.ok) {
      const newLink = await response.json();
      setLinkSuccess(`Invite link created: https://aspiredeck.com/invite/${slug}`);
      toast.success('Invite link created successfully');
      setNewSlug('');
      setNewUsageLimit('10');
      setShowLinkModal(false);
      // Refresh links
      const linksRes = await fetch('/api/whitelist/invite-links', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (linksRes.ok) {
        const linksData = await linksRes.json();
        setInviteLinks(linksData);
        setFilteredInviteLinks(linksData);
      }
    } else {
      const err = await response.json();
      setLinkError(err.message || 'Failed to create link');
      toast.error(err.message || 'Failed');
    }
  } catch (err) {
    setLinkError('Network error');
    toast.error('Network error');
  } finally {
    setLoading(false);
  }
};

// === NEW: Approve a user from waiting list ===
const handleApproveWaiting = async (userId, email) => {
  if (!window.confirm(`Approve ${email} and add to whitelist?`)) return;

  setApprovingId(userId);
  try {
    const token = localStorage.getItem('adminToken');
    const res = await fetch('/api/whitelist/approve-waiting', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ userId, email })
    });

    if (res.ok) {
      toast.success(`Approved ${email} and sent invitation email`);
      fetchWaitingList(); // Refresh list
    } else {
      const err = await res.json();
      toast.error(err.message || 'Failed to approve');
    }
  } catch (err) {
    toast.error('Network error');
  } finally {
    setApprovingId(null);
  }
};

  return (
    <div className="specific-page admin-dashboard-wrapper mt-4">
      <div className="card shadow p-4">
        <header className="admin-dashboard-header text-center mb-4">
          <div className="admin-dashboard-logo"></div>
          <h1 className="text-center mb-4">Admin Dashboard</h1>
        </header>
        <nav className="mb-4">
          <ul className="nav nav-pills">
            <li className="nav-item"><Link className="nav-link" to="/admin28976730">Manage Questions</Link></li>
            <li className="nav-item"><Link className="nav-link" to="/facet-management28976730">Manage Facets</Link></li>
            <li className="nav-item"><Link className="nav-link active" to="/whitelist-manager28976730">Whitelist Manager</Link></li>
            <li className="nav-item"><Link className="nav-link" to="/jobpost-management28976730">Job Post Manager</Link></li>
            <li className="nav-item"><Link className="nav-link" to="/supercurricularactivity-management28976730">SuperCurricular Manager</Link></li>
            <li className="nav-item"><Link className="nav-link" to="/star-experience-manager28976730">STAR Experiences</Link></li> 
            <li className="nav-item"><Link className="nav-link" to="/top-interview-questions-manager28976730">Interview Questions</Link></li> 
            <li className="nav-item"><Link className="nav-link" to="/personal-statement-guidance-manager28976730">Personal Statement</Link></li>
            <li className="nav-item"><Link className="nav-link" to="/rss-feed-manager28976730">RSS Feeds</Link></li>            
            <li className="nav-item"><Link className="nav-link" to="/ai-skill-development-manager28976730">AI Skill Development</Link></li>
            <li className="nav-item"><Link className="nav-link" to="/knowledge-cms-manager28976730">Knowledge CMS</Link></li>  
            <li className="nav-item"><Link className="nav-link" to="/investor-deck-manager28976730">Investor Deck</Link></li>
            <li className="nav-item"><Link className="nav-link" to="/free-courses-manager28976730">Free Courses</Link></li>
            <li className="nav-item"><Link className="nav-link" to="/startup-tasks-cms28976730">Startup Tasks</Link></li>
            <li className="nav-item"><Link className="nav-link" to="/coach-the-coach-manager28976730">Coach the Coach</Link></li>
          </ul>
        </nav>

        <main className="whitelist-main">
          {error && (
            <div className="alert alert-error">
              <span>{error}</span>
              <button
                className="retry-button"
                onClick={fetchWhitelist}
                aria-label="Retry fetching data"
              >
                Retry
              </button>
            </div>
          )}

          {successMessage && <div className="alert alert-success">{successMessage}</div>}

{/* Grant Invite Credits Section - Admin Only */}
<section className="add-entry-section mt-5">
  <h3 className="subsection-title-grant">
    Grant Invite Credits
  </h3>
  <button
    className="btn btn-outline-warning mb-3"
    onClick={() => setShowCreditModal(true)}
  >
    Grant Extra Invite Credits to a User
  </button>
  <h3 className="subsection-title-grant">
    URL Invite Links
  </h3>
  <p className="text-muted mb-3">
    Create a special signup link that bypasses whitelisting. Set how many times it can be used before excess signups go to waiting list.
  </p>
  <button
    className="btn btn-outline-success mb-3"
    onClick={() => setShowLinkModal(true)}
  >
    Create New Invite Link
  </button>
</section>

{/* Create Invite Link Modal */}
{showLinkModal && (
  <div className="modal-overlay">
    <div className="modal-content">
      <div className="modal-header">
        <h5 className="modal-title">Create Student Creator Invite Link</h5>
        <button
          className="modal-close"
          onClick={() => {
            setShowLinkModal(false);
            setLinkError('');
            setLinkSuccess('');
          }}
        >
          ×
        </button>
      </div>
      <div className="modal-body">
        <form onSubmit={handleCreateInviteLink}>
          <div className="form-group mb-3">
            <label>URL Slug</label>
            <input
              type="text"
              className="form-control"
              value={newSlug}
              onChange={(e) => setNewSlug(e.target.value)}
              placeholder="e.g., johncreator2025"
              required
            />
            <small className="text-muted">
              Full link will be: https://aspiredeck.com/invite/<strong>{newSlug || 'slug'}</strong>
            </small>
          </div>
          <div className="form-group mb-3">
            <label>Maximum Uses (before waiting list)</label>
            <input
              type="number"
              min="1"
              className="form-control"
              value={newUsageLimit}
              onChange={(e) => setNewUsageLimit(e.target.value)}
              required
            />
            <small className="text-muted">
              After this limit, additional signups will require manual admin approval.
            </small>
          </div>
          {linkError && <div className="alert alert-danger mt-2">{linkError}</div>}
          {linkSuccess && <div className="alert alert-success mt-2">{linkSuccess}</div>}
          <div className="modal-footer">
            <button
              type="button"
              className="modal-button cancel"
              onClick={() => setShowLinkModal(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="modal-button delete"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Link'}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
)}

{/* Grant Credits Modal */}
{showCreditModal && (
  <div className="modal-overlay">
    <div className="modal-content">
      <div className="modal-header">
        <h5 className="modal-title">Grant Invite Credits</h5>
        <button
          className="modal-close"
          onClick={() => {
            setShowCreditModal(false);
            setCreditError('');
            setCreditSuccess('');
          }}
        >
          ×
        </button>
      </div>
      <div className="modal-body">
        <form onSubmit={handleGrantInviteCredits}>
          <div className="form-group mb-3">
            <label>User Email</label>
            <input
              type="email"
              className="form-control"
              value={creditUserEmail}
              onChange={(e) => setCreditUserEmail(e.target.value)}
              placeholder="user@example.com"
              required
            />
          </div>
          <div className="form-group mb-3">
            <label>Number of Invite Credits to Add</label>
            <input
              type="number"
              min="1"
              className="form-control"
              value={creditAmount}
              onChange={(e) => setCreditAmount(e.target.value)}
              required
            />
          </div>
          {creditError && <div className="alert alert-danger mt-2">{creditError}</div>}
          {creditSuccess && <div className="alert alert-success mt-2">{creditSuccess}</div>}
          <div className="modal-footer">
            <button
              type="button"
              className="modal-button cancel"
              onClick={() => setShowCreditModal(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="modal-button delete"
              disabled={loading}
            >
              {loading ? 'Granting...' : 'Grant Credits'}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
)}
          <section className="add-entry-section">
            <h3 className="subsection-title">Add Whitelist Entry</h3>
            <form onSubmit={handleAddEntry} className="add-entry-form">
              <div className="form-row">
                <div className="form-group">
                  <select
                    className="form-select"
                    value={entryType}
                    onChange={(e) => {
                      setEntryType(e.target.value);
                      setNewEntry('');
                      setError('');
                    }}
                    disabled={loading}
                    aria-label="Select entry type"
                  >
                    <option value="domain">Domain</option>
                    <option value="email">Email</option>
                  </select>
                </div>
                <div className="form-group input-group">
                  <input
                    type="text"
                    className={`form-input ${error ? 'is-invalid' : ''}`}
                    placeholder={entryType === 'domain' ? 'e.g., example.com' : 'e.g., user@domain.com'}
                    value={newEntry}
                    onChange={(e) => {
                      setNewEntry(e.target.value);
                      setError(validateEntry(e.target.value, entryType));
                    }}
                    disabled={loading}
                    aria-label={entryType === 'domain' ? 'Domain input' : 'Email input'}
                  />
                  {error && <div className="invalid-feedback">{error}</div>}
                </div>

      {/* NEW: Label Selector */}
      <div className="form-group">
        <select
          className="form-select"
          value={selectedLabel}
          onChange={(e) => setSelectedLabel(e.target.value)}
        >
          <option value="standard">Standard</option>
          <option value="coachplus">CoachPlus</option>
          <option value="leadershipplus">LeadershipPlus</option>
        </select>
      </div>

                <div className="form-group">
                  <button
                    type="submit"
                    className="add-button"
                    disabled={loading || !!validateEntry(newEntry, entryType)}
                    title="Add whitelist entry"
                    aria-label="Add whitelist entry"
                  >
                    {loading ? (
                      <span className="spinner" role="status" aria-hidden="true"></span>
                    ) : (
                      'Add'
                    )}
                  </button>
                </div>
              </div>
            </form>
          </section>

          <section className="search-section">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search domains or emails..."
              className="search-input"
              aria-label="Search domains or emails"
              disabled={loading}
            />
          </section>

                  <section className="list-section">
            <h3 className="subsection-title">Whitelisted Domains</h3>
            {loading ? (
              <div className="loading">
                <span className="spinner" role="status" aria-hidden="true"></span> Loading domains...
              </div>
            ) : filteredDomains.length === 0 ? (
              <p className="no-data">No domains found.</p>
            ) : (
              <>
                <table className="whitelist-table">
                  <thead>
                    <tr>
                      <th>Domain</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDomains.slice(0, showAllDomains ? filteredDomains.length : 5).map((domain) => (
                      <tr key={domain} className="table-row">
                        <td>{domain}</td>
                        <td>
                          <button
                            className="delete-button"
                            onClick={() => confirmDelete('domain', domain)}
                            title="Delete domain"
                            disabled={loading}
                            aria-label={`Delete domain ${domain}`}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredDomains.length > 5 && (
                  <div className="text-center mt-3">
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => setShowAllDomains(!showAllDomains)}
                    >
                      {showAllDomains ? 'Show Less' : `Show All (${filteredDomains.length})`}
                    </button>
                  </div>
                )}
              </>
            )}
          </section>

          <section className="list-section">
            <h3 className="subsection-title">Whitelisted Emails</h3>
            {loading ? (
              <div className="loading">
                <span className="spinner" role="status" aria-hidden="true"></span> Loading emails...
              </div>
            ) : filteredEmails.length === 0 ? (
              <p className="no-data">No emails found.</p>
            ) : (
              <>
                <table className="whitelist-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmails.slice(0, showAllEmails ? filteredEmails.length : 5).map((email) => (
                      <tr key={email} className="table-row">
                        <td>{email}</td>
                        <td>
                          <button
                            className="delete-button"
                            onClick={() => confirmDelete('email', email)}
                            title="Delete email"
                            disabled={loading}
                            aria-label={`Delete email ${email}`}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredEmails.length > 5 && (
                  <div className="text-center mt-3">
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => setShowAllEmails(!showAllEmails)}
                    >
                      {showAllEmails ? 'Show Less' : `Show All (${filteredEmails.length})`}
                    </button>
                  </div>
                )}
              </>
            )}
          </section>

<section className="list-section mt-5">
  <h3 className="subsection-title">Student Creator Invite Links</h3>
  {loading ? (
    <div className="loading">
      <span className="spinner" role="status" aria-hidden="true"></span> Loading links...
    </div>
  ) : filteredInviteLinks.length === 0 ? (
    <p className="no-data">No invite links created yet.</p>
  ) : (
    <>
      <table className="whitelist-table">
        <thead>
          <tr>
            <th>Slug</th>
            <th>Full Link</th>
            <th>Uses / Limit</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredInviteLinks.slice(0, showAllLinks ? filteredInviteLinks.length : 5).map((link) => (
            <tr key={link._id || link.slug}>
              <td><code>{link.slug}</code></td>
              <td>
                <a href={`https://aspiredeck.com/invite/${link.slug}`} target="_blank" rel="noopener noreferrer">
                  https://aspiredeck.com/invite/{link.slug}
                </a>
              </td>
              <td>{link.usedCount || 0} / {link.usageLimit}</td>
              <td>
                <span className={`badge ${link.usedCount >= link.usageLimit ? 'bg-warning' : 'bg-success'}`}>
                  {link.usedCount >= link.usageLimit ? 'Limited' : 'Active'}
                </span>
              </td>
              <td>
                <button
                  className="delete-button"
                  onClick={() => confirmDelete('inviteLink', link.slug)}
                  title="Delete invite link"
                  disabled={loading}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {filteredInviteLinks.length > 5 && (
        <div className="text-center mt-3">
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => setShowAllLinks(!showAllLinks)}
          >
            {showAllLinks ? 'Show Less' : `Show All (${filteredInviteLinks.length})`}
          </button>
        </div>
      )}
    </>
  )}
</section>
{/* === NEW: Waiting List Section === */}
<section className="list-section mt-5">
  <h3 className="subsection-title">Waiting List (Invite Limit Exceeded)</h3>
  {loading ? (
    <div className="loading">
      <span className="spinner" role="status" aria-hidden="true"></span> Loading waiting list...
    </div>
  ) : filteredWaitingList.length === 0 ? (
    <p className="no-data">No users on waiting list.</p>
  ) : (
    <>
      <table className="whitelist-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Invite Slug</th>
            <th>Requested At</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredWaitingList.slice(0, showAllWaiting ? filteredWaitingList.length : 10).map((item) => (
            <tr key={item.id}>
              <td>{item.email}</td>
              <td><code>{item.slug || 'N/A'}</code></td>
              <td>{new Date(item.requested_at).toLocaleString()}</td>
              <td>
                <button
                  className="btn btn-success btn-sm"
                  onClick={() => handleApproveWaiting(item.id, item.email)}
                  disabled={approvingId === item.id}
                >
                  {approvingId === item.id ? 'Approving...' : 'Approve & Notify'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {filteredWaitingList.length > 10 && (
        <div className="text-center mt-3">
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => setShowAllWaiting(!showAllWaiting)}
          >
            {showAllWaiting ? 'Show Less' : `Show All (${filteredWaitingList.length})`}
          </button>
        </div>
      )}
    </>
  )}
</section>
          {/* Delete Confirmation Modal */}
          {showDeleteModal && (
            <div className="modal-overlay">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Deletion</h5>
                  <button
                    className="modal-close"
                    onClick={() => setShowDeleteModal(false)}
                    aria-label="Close modal"
                  >
                    ×
                  </button>
                </div>
                <div className="modal-body">
                  <p>
                    Are you sure you want to remove <strong>{deleteCandidate?.value}</strong> from the whitelist?
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    className="modal-button cancel"
                    onClick={() => setShowDeleteModal(false)}
                    aria-label="Cancel deletion"
                  >
                    Cancel
                  </button>
                  <button
                    className="modal-button delete"
                    onClick={handleDeleteEntry}
                    disabled={loading}
                    aria-label="Confirm deletion"
                  >
                    {loading ? (
                      <span className="spinner" role="status" aria-hidden="true"></span>
                    ) : (
                      'Delete'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>

        <ToastContainer position="top-right" autoClose={3000} hideProgressBar closeOnClick />
      </div>
    </div>
  );
};

export default WhitelistManager;